import { IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IOption } from '@gcl/react-core';
import { useEffect, useMemo, useState } from 'react';
import { useProjectId } from '@config/config.hooks';
import { QueriesObserver, QueryKey, useQueryClient } from '@tanstack/react-query';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { IProjectRole } from '@role/projectRole/projectRole.models';
import { OPTION_KEY_ALL_COMPANIES } from './userList.constants';

export const getIsCompanyEditable = (isReadOnly: boolean, companyOption?: IOption<IProjectCompanyOption>) => {
  return isReadOnly ? false : (companyOption && isPositiveNumber(companyOption.data?.id)) ?? false;
};

export const useSelectedCompany = (isReadOnly: boolean) => {
  const [selectedCompanyOption, setSelectedCompanyOption] = useState<IOption<IProjectCompanyOption> | undefined>(
    undefined,
  );
  const [selectedCompanyForForm, setSelectedCompanyForForm] = useState<IOption<IProjectCompanyOption> | undefined>(
    undefined,
  );

  useEffect(() => {
    if (selectedCompanyOption?.key !== OPTION_KEY_ALL_COMPANIES) {
      setSelectedCompanyForForm(selectedCompanyOption);
    }
  }, [JSON.stringify(selectedCompanyOption)]);

  const isCompanyEditable = useMemo(() => {
    return getIsCompanyEditable(isReadOnly, selectedCompanyOption);
  }, [selectedCompanyOption, isReadOnly]);

  return {
    selectedCompanyOption,
    setSelectedCompanyOption,
    selectedCompanyForForm,
    setSelectedCompanyForForm,
    isCompanyEditable,
  };
};

export const useMaxRoleCols = (
  companyOptions: IOption<IProjectCompanyOption>[],
  selectedCompanyOption?: IOption<IProjectCompanyOption>,
) => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  const [maxCols, setMaxCols] = useState(0);

  const queryKeys = useMemo(() => {
    return companyOptions.map((o) => {
      return { queryKey: [projectId, o.data?.id, FETCH_QUERIES_NAME.ROLES_WITH_USER] as QueryKey };
    });
  }, [JSON.stringify(companyOptions)]);

  useEffect(() => {
    const observer = new QueriesObserver(queryClient, queryKeys);
    const unsubscribe = observer?.subscribe((result) => {
      const counts = result.map((r) => ((r.data ?? []) as IProjectRole[]).length).filter((c): c is number => !!c);
      setMaxCols(Math.max(...counts, 0));
    });

    return () => {
      unsubscribe();
    };
  }, [selectedCompanyOption, queryKeys]);

  return maxCols;
};
